if (!customElements.get('upsell-item')) {
  class UpsellItem extends HTMLElement {
    constructor() {
      super();

      this.checkbox = this.querySelector('.upsell-item__checkbox');
      this.variantsWrapper = this.querySelector('.upsell-item__variants');
      this.variantButtons = this.querySelectorAll('.upsell-item__swatch');
      this.variantSelect = this.querySelector('.upsell-item__select');
      this.priceEl = this.querySelector('.upsell-item__price');
      this.compareEl = this.querySelector('.upsell-item__compare');
      this.badgeEl = this.querySelector('.upsell-item__badge');
      this.mediaImages = this.querySelectorAll('.upsell-item__media-image');
      this.variantInput = this.querySelector('.upsell-item__input-id');
      this.quantityInput = this.querySelector('.upsell-item__input-quantity');
      this.addToCart = this.querySelector('product-block-add-to-cart');
    }

    connectedCallback() {
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.handleToggle.bind(this));
      }

      this.variantButtons.forEach((button) => {
        button.addEventListener('click', this.handleVariantChange.bind(this));
      });

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.handleSelectChange.bind(this));
      }

      this.syncState();
      this.syncAddToCartVariant();
    }

    handleToggle() {
      this.syncState();
    }

    handleVariantChange(event) {
      if (event.currentTarget.disabled) return;
      
      this.setActiveVariant(event.currentTarget);
      this.updatePrice();
      this.syncAddToCartVariant();
    }

    handleSelectChange(event) {
      const selectedOption = event.currentTarget.options[event.currentTarget.selectedIndex];
      if (!selectedOption || selectedOption.disabled) return;

      this.dataset.variantId = selectedOption.value || this.dataset.defaultVariantId || '';

      if (this.variantInput) {
        this.variantInput.value = this.dataset.variantId;
      }

      this.switchMedia(selectedOption.dataset.media);
      this.updatePrice();
      this.syncAddToCartVariant();
    }

    syncState() {
      if (this.checkbox?.checked && (this.variantButtons.length || this.variantSelect)) {
        this.ensureAvailableVariant();
      }

      this.updateSelectedVariant();
      this.updatePrice();

      const isSelected = this.checkbox?.checked ? 'true' : 'false';
      this.dataset.selected = isSelected;

      if (this.variantsWrapper) {
        this.variantsWrapper.setAttribute('aria-hidden', isSelected === 'true' ? 'false' : 'true');
      }

      if (this.variantInput) {
        this.variantInput.disabled = isSelected !== 'true';
        this.variantInput.value = this.dataset.variantId || this.dataset.defaultVariantId || '';
      }

      if (this.quantityInput) {
        this.quantityInput.disabled = isSelected !== 'true';
      }
    }

    ensureAvailableVariant() {
      if (this.variantSelect) {
        const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        if (selectedOption && !selectedOption.disabled) return;

        const firstAvailableOption = Array.from(this.variantSelect.options).find((option) => !option.disabled);
        if (firstAvailableOption) {
          this.variantSelect.value = firstAvailableOption.value;
          this.handleSelectChange({ currentTarget: this.variantSelect });
        }
        return;
      }

      const activeButton = this.getActiveVariantButton();
      if (activeButton && !activeButton.disabled) return;

      const firstAvailableButton = Array.from(this.variantButtons).find((button) => !button.disabled);
      if (firstAvailableButton) {
        this.setActiveVariant(firstAvailableButton);
      }
    }

    updateSelectedVariant() {
      if (this.variantSelect) {
        this.dataset.variantId = this.variantSelect.value || this.dataset.defaultVariantId || '';
        return;
      }

      const activeButton = this.getActiveVariantButton();
      if (activeButton) {
        this.dataset.variantId = activeButton.dataset.variantId;
        return;
      }

      this.dataset.variantId = this.dataset.defaultVariantId || '';
    }

    getActiveVariantButton() {
      return this.querySelector('.upsell-item__swatch.is-active');
    }

    setActiveVariant(button) {
      this.variantButtons.forEach((variantButton) => {
        const isActive = variantButton === button;
        variantButton.classList.toggle('is-active', isActive);
        variantButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.dataset.variantId = button.dataset.variantId || this.dataset.defaultVariantId || '';
      if (this.variantInput) {
        this.variantInput.value = this.dataset.variantId;
      }
      this.switchMedia(button.dataset.media);
    }

    syncAddToCartVariant() {
      if (!this.addToCart || !this.dataset.variantId || typeof this.addToCart.updateSelectedVariantId !== 'function') return;
      this.addToCart.updateSelectedVariantId(this.dataset.variantId);
    }

    switchMedia(mediaId) {
      if (!mediaId || !this.mediaImages.length) return;

      this.mediaImages.forEach((mediaImage) => {
        mediaImage.classList.toggle('upsell-item__media-image--active', mediaImage.dataset.mediaId === mediaId);
      });

      this.dataset.currentMediaId = mediaId;
    }

    updatePrice() {
      if (this.variantSelect) {
        const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        if (!selectedOption) return;

        if (this.priceEl) {
          this.priceEl.textContent = selectedOption.dataset.price || '';
        }

        if (this.compareEl) {
          this.compareEl.textContent = selectedOption.dataset.compareAtPrice || '';
          this.compareEl.hidden = !selectedOption.dataset.compareAtPrice;
        }

        if (this.badgeEl) {
          const discount = parseInt(selectedOption.dataset.discount || '0', 10);
          this.badgeEl.textContent = discount > 0 ? `${discount}% off` : '';
          this.badgeEl.hidden = discount <= 0;
        }

        return;
      }

      const activeButton = this.getActiveVariantButton();
      if (!activeButton) return;

      if (this.priceEl) {
        this.priceEl.textContent = activeButton.dataset.price || '';
      }

      if (this.compareEl) {
        this.compareEl.textContent = activeButton.dataset.compareAtPrice || '';
        this.compareEl.hidden = !activeButton.dataset.compareAtPrice;
      }

      if (this.badgeEl) {
        const discount = parseInt(activeButton.dataset.discount || '0', 10);
        this.badgeEl.textContent = discount > 0 ? `${discount}% off` : '';
        this.badgeEl.hidden = discount <= 0;
      }
    }
  }

  customElements.define('upsell-item', UpsellItem);
}
